/**
 * The specific-clients selector as plain data (ACR-1596): a searchable list, a
 * selection shown as removable chips, and no cap on how many clients can be
 * selected (spec: No cap on clients). The selection is an ordered list of tenant
 * ids; every function returns a new one.
 */

export interface ClientOption {
    readonly tenantId: string;
    readonly displayName: string;
    readonly slug?: string | undefined;
    readonly subdomain?: string | null | undefined;
}

export interface SelectionChip {
    readonly tenantId: string;
    readonly label: string;
}

/** Clients whose name, slug, subdomain or id contains the search, ignoring case. */
export function filterClients<T extends ClientOption>(
    clients: readonly T[],
    search: string,
): readonly T[] {
    const term = search.trim().toLowerCase();
    if (term === '') return clients;
    return clients.filter((client) =>
        [client.displayName, client.slug, client.subdomain, client.tenantId].some((field) =>
            field?.toLowerCase().includes(term),
        ),
    );
}

export function selectClients(
    selected: readonly string[],
    tenantIds: readonly string[],
): readonly string[] {
    const seen = new Set(selected);
    const added = tenantIds.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
    return added.length === 0 ? selected : [...selected, ...added];
}

export function selectClient(selected: readonly string[], tenantId: string): readonly string[] {
    return selectClients(selected, [tenantId]);
}

/** Removing a chip deselects that client. */
export function deselectClient(selected: readonly string[], tenantId: string): readonly string[] {
    return selected.filter((id) => id !== tenantId);
}

/** One chip per selected client, labelled by name; an id no longer in the list keeps the id. */
export function selectionChips(
    selected: readonly string[],
    clients: readonly ClientOption[],
): readonly SelectionChip[] {
    const names = new Map(clients.map((client) => [client.tenantId, client.displayName]));
    return selected.map((tenantId) => ({ tenantId, label: names.get(tenantId) ?? tenantId }));
}

/**
 * The merchant-provider filter (which payment provider a client processes with).
 * The providers come from a read of their own, so the selector has to work
 * without them: text search never depends on anything below.
 */

/** The select's "no provider chosen" value. */
export const ALL_PROVIDERS = '';

export interface ClientProviders {
    readonly tenantId: string;
    readonly providers: readonly string[];
}

export type ProvidersByTenant = ReadonlyMap<string, readonly string[]>;

export interface ProviderFilterView {
    /** Whether the select can be offered at all. */
    readonly available: boolean;
    /** `null` while the providers are unknown, so a stale choice filters nothing. */
    readonly byTenant: ProvidersByTenant | null;
    /** The providers present in the data, each once, sorted. Never a fixed list. */
    readonly options: readonly string[];
    readonly notice?: string;
}

export function providerFilterView(query: {
    readonly data: readonly ClientProviders[] | undefined;
    readonly isPending: boolean;
    readonly isError: boolean;
}): ProviderFilterView {
    if (query.data === undefined || query.isError) {
        return {
            available: false,
            byTenant: null,
            options: [],
            notice: query.isPending
                ? 'Loading merchant providers. The provider filter is unavailable until they load.'
                : 'Merchant providers could not be loaded. The provider filter is unavailable; search still works.',
        };
    }

    const byTenant = new Map(query.data.map((row) => [row.tenantId, row.providers]));
    const options = [...new Set(query.data.flatMap((row) => row.providers))].sort();
    return { available: true, byTenant, options };
}

/** The clients matching the text search and, when one is chosen and known, the provider. */
export function matchingClients<T extends ClientOption>(
    clients: readonly T[],
    filter: { readonly search: string; readonly provider: string },
    byTenant: ProvidersByTenant | null,
): readonly T[] {
    const ofProvider =
        filter.provider === ALL_PROVIDERS || byTenant === null
            ? clients
            : clients.filter((client) => byTenant.get(client.tenantId)?.includes(filter.provider));
    return filterClients(ofProvider, filter.search);
}

/** "Select all N matching": `added` is the N, `selected` the selection after it. */
export function selectAllMatching(
    selected: readonly string[],
    matches: readonly ClientOption[],
): { readonly added: number; readonly selected: readonly string[] } {
    const next = selectClients(
        selected,
        matches.map((client) => client.tenantId),
    );
    return { added: next.length - selected.length, selected: next };
}
